import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Check, ChevronUp, ChevronsUpDown, Download, LayoutGrid, ListFilter, Pencil, Plus, RefreshCw, Search, Table as TableIcon, Trash2, UserCog, Users } from "lucide-react";
import { 
  Popover as _Popover, 
  PopoverContent as _PopoverContent, 
  PopoverTrigger as _PopoverTrigger 
} from "../../components/ui/popover";
import { 
  Command as _Command, 
  CommandEmpty as _CommandEmpty, 
  CommandGroup as _CommandGroup, 
  CommandInput as _CommandInput, 
  CommandItem as _CommandItem, 
  CommandList as _CommandList 
} from "../../components/ui/command";
import { 
  Select as _Select, 
  SelectContent as _SelectContent, 
  SelectItem as _SelectItem, 
  SelectTrigger as _SelectTrigger, 
  SelectValue as _SelectValue 
} from "../../components/ui/select";

const Command = _Command as any;
const CommandEmpty = _CommandEmpty as any;
const CommandGroup = _CommandGroup as any;
const CommandInput = _CommandInput as any;
const CommandItem = _CommandItem as any;
const CommandList = _CommandList as any;
const Popover = _Popover as any;
const PopoverContent = _PopoverContent as any;
const PopoverTrigger = _PopoverTrigger as any;
const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;
import { cn } from "../../lib/utils";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { CopyButton } from "../../components/ui/copy-button";
import { AssignLeadModal } from "./assign-lead-modal";
import { CampaignModal } from "./campaign-modal";
import { LeadCallButton } from "./lead-call-button";
import { LeadFormModal } from "./lead-form-modal";
import type { CampaignField, Lead, LeadCampaign } from "./types";
import { avatarColorOf, contactIdOf, initialsOf, LEAD_STATUSES, leadStatus, SLA_REASON_LABELS, statusPillCls } from "./types";
import { CreateWorkflowBatchModal } from "./create-workflow-batch-modal";
import { UploadContactBatchModal } from "../workflows/upload-contact-batch-modal";
import { downloadLeadsCsv } from "./lead-export";
import { crmApi } from "./api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { Dropdown } from "../../components/ui/dropdown";

const ALL = "__all__";

function CampaignSelector({ campaigns, leads, activeCampaign, onSelect }: { campaigns: LeadCampaign[], leads: Lead[], activeCampaign: string, onSelect: (id: string) => void }) {
  const [open, setOpen] = useState(false);

  const items = useMemo(() => {
    return campaigns
      .map((c) => ({ ...c, count: leads.filter((l) => l.campaignId === c.id).length }))
      .sort((a, b) => b.count - a.count); // Sort by count descending so empty ones fall to the bottom
  }, [campaigns, leads]);

  const allCount = leads.length;

  const activeName = activeCampaign === ALL 
    ? "All campaigns" 
    : campaigns.find(c => c.id === activeCampaign)?.name || "All campaigns";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          className="flex min-w-[200px] cursor-pointer items-center justify-between gap-2 rounded-lg border border-line bg-elevated px-3 py-2 text-sm text-ink-2 hover:bg-soft"
        >
          <span className="truncate">{activeName}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search campaigns..." />
          <CommandList>
            <CommandEmpty>No campaigns match your search.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="All campaigns"
                onSelect={() => { onSelect(ALL); setOpen(false); }}
                className="flex cursor-pointer items-center justify-between data-[selected=true]:bg-soft data-[selected=true]:text-ink"
              >
                <div className="flex items-center gap-2">
                  <Check className={cn("h-4 w-4", activeCampaign === ALL ? "opacity-100" : "opacity-0")} />
                  <span className="font-medium">All campaigns</span>
                </div>
                <span className="rounded-full bg-soft px-2 py-0.5 text-[10px] font-semibold text-ink-3">
                  {allCount}
                </span>
              </CommandItem>
              {items.map((campaign) => (
                <CommandItem
                  key={campaign.id}
                  value={campaign.name}
                  onSelect={() => { onSelect(campaign.id); setOpen(false); }}
                  className="flex cursor-pointer items-center justify-between data-[selected=true]:bg-soft data-[selected=true]:text-ink"
                >
                  <div className="flex items-center gap-2">
                    <Check className={cn("h-4 w-4", activeCampaign === campaign.id ? "opacity-100" : "opacity-0")} />
                    <span>{campaign.name}</span>
                  </div>
                  <span className="rounded-full bg-soft px-2 py-0.5 text-[10px] font-semibold text-ink-3">
                    {campaign.count}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function LeadsPage({ customers = false }: { customers?: boolean }) {
  const cache = useQueryClient();
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  // Breach list is the notification surface (Phase 2C #14 owns push alerts).
  // Server-scoped to accessible CRM records; the page's filters narrow the
  // badge count below so customer breaches do not appear on the Leads page.
  const overdueQuery = useQuery({ queryKey: ["crm-sla-overdue"], queryFn: crmApi.slaOverdue, refetchInterval: 60000 });
  const overdueMap = useMemo(
    () => new Map((overdueQuery.data ?? []).map(row => [row.id, row.reasons])),
    [overdueQuery.data],
  );
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [agentFilter, setAgentFilter] = useState(ALL);
  const [workflowFilter, setWorkflowFilter] = useState(ALL);
  const [dialedByFilter, setDialedByFilter] = useState(ALL);
  const [campaignNameFilter, setCampaignNameFilter] = useState(ALL);
  const [whatsappInstanceFilter, setWhatsappInstanceFilter] = useState(ALL);
  const CHANNELS = ["whatsapp", "email", "sms"] as const;
  const [channelFilter, setChannelFilter] = useState<Set<string>>(() => new Set());
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({ ...presetRange("last_30"), preset: "last_30" }));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // v2 keys: the schema changed (campaigns now carry a form-field
  // definition, leads store dynamic `values`) — a v1-shaped payload in
  // localStorage would otherwise render with broken/mismatched campaigns.
  const [campaigns, setCampaigns] = useState<LeadCampaign[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [customStatuses, setCustomStatuses] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const statuses = Array.from(new Set([...LEAD_STATUSES, ...customStatuses, ...leads.map(leadStatus)]));
  // Fresh lead and New are the same early stage — they live in Leads only.
  // Customers board + status filter hide them to avoid an empty duplicate column.
  const isEarlyStatus = (s: string) => {
    const low = s.trim().toLowerCase();
    return low === "fresh lead" || low === "new";
  };
  const visibleStatuses = useMemo(
    () => (customers ? statuses.filter((s) => !isEarlyStatus(s)) : statuses),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers, statuses.join("|")]
  );
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const reload = async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setLoading(true);
    setError("");
    try {
      const [nextCampaigns, nextLeads] = await Promise.all([crmApi.campaigns(), crmApi.leads()]);
      setCampaigns(nextCampaigns); setLeads(nextLeads.filter(lead => customers ? !lead.fresh : lead.fresh));
      void cache.invalidateQueries({ queryKey: ["crm-sla-overdue"] });
    } catch (e) {
      // A background poll failing (e.g. a flaky connection) shouldn't blank
      // out an already-loaded list with an error banner — only surface it
      // when there's nothing on screen yet.
      if (!opts.silent) setError(e instanceof Error ? e.message : "Unable to load CRM data.");
    }
    finally { if (!opts.silent) setLoading(false); }
  };
  useEffect(() => {
    void reload(); // CRM data is loaded from Core, never localStorage.

    // Leads also arrive from outside this tab entirely — someone else's
    // session, or a public form submission from an external visitor — so a
    // one-time fetch on mount misses anything that shows up afterward.
    // Poll quietly, and refetch immediately whenever the tab regains focus
    // (covers switching back from the phone's dialer/WhatsApp/etc.).
    const interval = window.setInterval(() => void reload({ silent: true }), 20_000);
    const onFocus = () => void reload({ silent: true });
    const onVisible = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeCampaign, setActiveCampaign] = useState<string>(() => searchParams.get("campaign") || ALL);
  // Source filter: pick which kind of lead origin to narrow to, then (for
  // campaign/batch) a second dropdown appears to pick which one.
  const [sourceFilter, setSourceFilter] = useState<"all" | "campaign" | "batch" | "manual">(() =>
    searchParams.get("campaign") ? "campaign" : "all"
  );
  const [activeBatch, setActiveBatch] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (sourceFilter !== "all") count++;
    if (agentFilter !== ALL) count++;
    if (workflowFilter !== ALL) count++;
    if (dialedByFilter !== ALL) count++;
    if (campaignNameFilter !== ALL) count++;
    if (whatsappInstanceFilter !== ALL) count++;
    if (channelFilter.size > 0) count++;
    if (dateRange.preset !== "last_30" || !dateRange.preset) count++;
    return count;
  }, [sourceFilter, agentFilter, workflowFilter, dialedByFilter, campaignNameFilter, whatsappInstanceFilter, channelFilter, dateRange]);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [uploadBatchOpen, setUploadBatchOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "board">(() => {
    try {
      return window.localStorage.getItem(customers ? "crm-customers-view" : "crm-leads-view") === "board" ? "board" : "table";
    } catch { return "table"; }
  });
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropCol, setDropCol] = useState<string | null>(null);
  const [dragBusy, setDragBusy] = useState(false);

  const switchView = (mode: "table" | "board") => {
    setViewMode(mode);
    try { window.localStorage.setItem(customers ? "crm-customers-view" : "crm-leads-view", mode); } catch { /* ignore */ }
  };

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<LeadCampaign | null>(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [assigningLead, setAssigningLead] = useState<Lead | null>(null);

  const selectCampaign = (campaignId: string) => {
    setActiveCampaign(campaignId);
    const next = new URLSearchParams(searchParams);
    if (campaignId === ALL) next.delete("campaign");
    else next.set("campaign", campaignId);
    setSearchParams(next);
  };

  useEffect(() => {
    const campaignFromUrl = searchParams.get("campaign") || ALL;
    if (campaignFromUrl === ALL || campaigns.some((campaign) => campaign.id === campaignFromUrl)) {
      setActiveCampaign(campaignFromUrl);
      if (campaignFromUrl !== ALL) setSourceFilter("campaign");
      return;
    }
    setActiveCampaign(ALL);
    const next = new URLSearchParams(searchParams);
    next.delete("campaign");
    setSearchParams(next, { replace: true });
  }, [campaigns, searchParams, setSearchParams]);

  // Batches a lead could have come from, derived from the leads already
  // loaded rather than a separate fetch — enough for the Source filter below.
  const batchOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of leads) {
      if (l.batchId) map.set(l.batchId, l.batchName || l.batchId);
    }
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [leads]);

  const campaignName = useMemo(() => {
    const map = new Map(campaigns.map((c) => [c.id, c.name]));
    return (id: string) => map.get(id) ?? "—";
  }, [campaigns]);

  // What created this lead, for the Campaign column: a real campaign by
  // name, a batch upload by its batch name, or a manually-added lead.
  const sourceLabel = useMemo(() => {
    return (l: Lead) => {
      if (l.campaignId) return campaignName(l.campaignId);
      if (l.source === "Batch") return l.batchName ? `Batch: ${l.batchName}` : "Batch";
      if (!l.source || l.source === "Manual") return "Manual";
      return l.source;
    };
  }, [campaignName]);

  const memberIds = useMemo(() => new Set((team.data?.members ?? []).map((m) => m.id)), [team.data]);

  const agentName = useMemo(() => {
    const map = new Map((team.data?.members ?? []).map((m) => [m.id, m.name]));
    return (id?: string | null) => {
      if (!id) return "Unassigned";
      const found = map.get(id);
      if (found && !/^[0-9a-f-]{25,}$/i.test(found)) return found;
      return "Former agent";
    };
  }, [team.data]);

  // Derived from activeCampaign so it always matches whichever campaign is
  // currently selected, instead of a snapshot taken whenever the button was
  // last clicked (which used to keep showing the previous campaign's link
  // after switching campaigns).
  const previewLink = useMemo(() => {
    const form = campaigns.find((c) => c.id === activeCampaign);
    if (!form) return "";
    return `${window.location.origin}${form.form_url || `/forms/crm/${form.token || form.id}`}`;
  }, [campaigns, activeCampaign]);

  const previewBoxRef = useRef<HTMLDivElement>(null);
  const previewBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!previewOpen) return;
    // "click" (not "mousedown") so the box only closes once the click that
    // landed elsewhere has already been handled by its own target — closing
    // on mousedown collapses this box and reflows the layout before the
    // browser's matching "click" fires, so the cursor ends up over the wrong
    // element (e.g. a campaign chip shifting up) and that click is lost.
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (previewBoxRef.current?.contains(target) || previewBtnRef.current?.contains(target)) return;
      setPreviewOpen(false);
    };
    document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, [previewOpen]);

  const matchingLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999);
    return leads.filter((l) => {
      if (agentFilter !== ALL) {
        if (agentFilter === "unassigned") {
          if (l.assignedAgentId) return false;
        } else if (agentFilter === "former") {
          if (!l.assignedAgentId || memberIds.has(l.assignedAgentId)) return false;
        } else if (l.assignedAgentId !== agentFilter) {
          return false;
        }
      }
      if (sourceFilter === "campaign") {
        if (!l.campaignId) return false;
        if (activeCampaign !== ALL && l.campaignId !== activeCampaign) return false;
      } else if (sourceFilter === "batch") {
        if (!l.batchId) return false;
        if (activeBatch !== ALL && l.batchId !== activeBatch) return false;
      } else if (sourceFilter === "manual") {
        if (l.campaignId || l.batchId) return false;
      }
      if (statusFilter !== ALL && leadStatus(l) !== statusFilter) return false;
      if (workflowFilter !== ALL && (l.workflowName || "") !== workflowFilter) return false;
      if (dialedByFilter !== ALL && (l.lastCallAgentName || "") !== dialedByFilter) return false;
      if (campaignNameFilter !== ALL && (l.campaignName || "") !== campaignNameFilter) return false;
      if (whatsappInstanceFilter !== ALL && (l.whatsappInstance || "") !== whatsappInstanceFilter) return false;
      if (channelFilter.size > 0 && !(l.sentChannels || []).some((c) => channelFilter.has(c))) return false;
      const createdAt = new Date(l.createdAt);
      if (Number.isNaN(createdAt.getTime()) || createdAt < dateRange.start || createdAt > end) return false;
      if (!q) return true;
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.phone || "").toLowerCase().includes(q) ||
        (l.contactId || l.id).toLowerCase().includes(q) ||
        Object.values(l.values || {}).some((v) => v.toLowerCase().includes(q)) ||
        (l.company || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q)
      );
    });
  }, [leads, sourceFilter, activeCampaign, activeBatch, search, statusFilter, agentFilter, workflowFilter, dialedByFilter, campaignNameFilter, whatsappInstanceFilter, channelFilter, dateRange, memberIds]);

  // Workflow + agent options for the filters below, derived from the leads
  // already loaded — "which agent" is scoped to whichever workflow is picked,
  // since the same agent name can appear in more than one workflow.
  const workflowOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.workflowName).filter((n): n is string => !!n))).sort(),
    [leads],
  );
  const dialedByOptions = useMemo(() => {
    const pool = workflowFilter === ALL ? leads : leads.filter((l) => l.workflowName === workflowFilter);
    return Array.from(new Set(pool.map((l) => l.lastCallAgentName).filter((n): n is string => !!n))).sort();
  }, [leads, workflowFilter]);
  // WhatsApp Campaign + sending-instance options, same derive-from-loaded-leads
  // pattern as workflow/dialed-by above.
  const campaignNameOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.campaignName).filter((n): n is string => !!n))).sort(),
    [leads],
  );
  const whatsappInstanceOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.whatsappInstance).filter((n): n is string => !!n))).sort(),
    [leads],
  );

  // Count exactly the records the Overdue toggle would show, independently
  // of whether that toggle is currently on (and across both table and board).
  const overdueLeads = useMemo(
    () => matchingLeads.filter(lead => overdueMap.has(lead.id)),
    [matchingLeads, overdueMap],
  );
  const overdueCount = overdueLeads.length;

  const SORT_OPTIONS = [
    { value: "modified_desc", label: "Recently modified" },
    { value: "modified_asc", label: "Oldest modified" },
    { value: "created_desc", label: "Latest created" },
    { value: "created_asc", label: "Oldest created" },
    { value: "name_asc", label: "Name A-Z" },
    { value: "name_desc", label: "Name Z-A" },
  ] as const;
  const [sortOrder, setSortOrder] = useState<(typeof SORT_OPTIONS)[number]["value"]>("modified_desc");
  const sortLeads = (rows: Lead[]) => {
    const byDate = (field: "updatedAt" | "createdAt", lead: Lead) => {
      const value = field === "updatedAt" ? lead.updatedAt || lead.createdAt : lead.createdAt;
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? 0 : time;
    };
    return [...rows].sort((a, b) => {
      switch (sortOrder) {
        case "modified_desc": return byDate("updatedAt", b) - byDate("updatedAt", a);
        case "modified_asc": return byDate("updatedAt", a) - byDate("updatedAt", b);
        case "created_desc": return byDate("createdAt", b) - byDate("createdAt", a);
        case "created_asc": return byDate("createdAt", a) - byDate("createdAt", b);
        case "name_asc": return (a.name || "").localeCompare(b.name || "");
        case "name_desc": return (b.name || "").localeCompare(a.name || "");
        default: return 0;
      }
    });
  };
  const filteredLeads = useMemo(
    () => sortLeads(showOverdueOnly ? overdueLeads : matchingLeads),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showOverdueOnly, overdueLeads, matchingLeads, sortOrder],
  );

  // Clicking a row opens the full record page (reference-style view),
  // not a slide-over drawer.
  const openLead = (id: string) => navigate(`/crm/${customers ? "customers" : "leads"}/${id}`);

  const editCampaign = async (name: string, fields: CampaignField[]) => {
    if (!editingCampaign) return;
    await crmApi.updateCampaign(editingCampaign.id, name, fields);
    await reload();
    setEditingCampaign(null);
  };

  const addLead = async (data: Omit<Lead, "id" | "fresh" | "createdAt"> & { allowDuplicate?: boolean }) => {
    const lead = await crmApi.createLead({ ...data, fresh: false });
    navigate(`/crm/${customers ? "customers" : "leads"}/${lead.id}`);
  };

  const updateLead = async (id: string, data: Partial<Omit<Lead, "id" | "createdAt">>) => {
    try { await crmApi.updateLead(id, data); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to save lead."); return; }
    await reload({ silent: true });
  };

  const deleteLead = async (id: string) => {
    try { await crmApi.deleteLead(id); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to remove lead."); return; }
    setLeads((prev) => prev.filter((l) => l.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const deleteSelectedLeads = async () => {
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => crmApi.deleteLead(id)));
    const deletedIds = new Set(ids.filter((_, i) => results[i].status === "fulfilled"));
    const failed = results.filter((r) => r.status === "rejected").length;
    setLeads((prev) => prev.filter((l) => !deletedIds.has(l.id)));
    setSelectedIds(new Set());
    setPendingBulkDelete(false);
    if (failed > 0) setError(`${failed} of ${ids.length} ${ids.length === 1 ? "lead" : "leads"} could not be removed.`);
  };

  const moveLeadToStatus = async (id: string, status: string) => {
    const lead = leads.find((l) => l.id === id);
    if (!lead || dragBusy) return;
    if (leadStatus(lead) === status) return;
    setDragBusy(true);
    // Optimistic move so the card lands instantly; reload reconciles.
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status, fresh: status === "Fresh lead" } : l)));
    try {
      await crmApi.updateLead(id, { status, fresh: status === "Fresh lead" });
      await reload({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to move lead.");
      await reload({ silent: true });
    } finally {
      setDragBusy(false);
      setDragId(null);
      setDropCol(null);
    }
  };

  const boardGroups = useMemo(() => {
    const map = new Map<string, Lead[]>();
    visibleStatuses.forEach((s) => map.set(s, []));
    filteredLeads.forEach((l) => {
      const s = leadStatus(l);
      // Early-stage strays in Customers fall back to the first visible column
      // so no card ever disappears from the board.
      const key = map.has(s) ? s : visibleStatuses[0];
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(l);
    });
    return visibleStatuses.map((s) => ({ status: s, items: map.get(s) ?? [] }));
  }, [filteredLeads, visibleStatuses]);

  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const requestDelete = (id: string) => {
    const lead = leads.find((l) => l.id === id);
    setPendingDelete({ id, name: lead?.name || lead?.contactId || id });
  };

  const toggleLead = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectVisibleLeads = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredLeads.forEach((lead) => checked ? next.add(lead.id) : next.delete(lead.id));
      return next;
    });
  };

  const applyBulkStatus = async (status: string) => {
    if (!status) return;
    try { await Promise.all(Array.from(selectedIds, id => crmApi.updateLead(id, { status, fresh: status === "Fresh lead" }))); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to update selected leads."); return; }
    setSelectedIds(new Set());
    await reload({ silent: true });
  };

  const visibleSelectedCount = filteredLeads.filter((lead) => selectedIds.has(lead.id)).length;
  const allVisibleSelected = filteredLeads.length > 0 && visibleSelectedCount === filteredLeads.length;

  return (
    <div className="flex flex-col p-4 sm:p-5 md:p-6">
      {/* ── Top Header Row: Title + Overdue Tag + View Switcher + Tools + Add Button ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 pb-2.5">
        {/* Left: Title + Count + Overdue Tag */}
        <div className="flex items-center gap-2">
          <h1 className="text-sm font-semibold text-ink">{customers ? "Customers" : "Leads"}</h1>
          <span className="font-mono text-xs rounded-full bg-soft px-2 py-0.5 text-ink-3">
            {leads.length}
          </span>
          {overdueCount > 0 && (
            <button
              onClick={() => setShowOverdueOnly(v => !v)}
              className={cn(
                "font-mono text-xs rounded-full px-2 py-0.5 font-medium transition-colors",
                showOverdueOnly 
                  ? "bg-red-600 text-white shadow-xs" 
                  : "bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20"
              )}
              title={showOverdueOnly ? "Show all" : "Show overdue only"}
            >
              {overdueCount} overdue
            </button>
          )}
        </div>

        {/* Right: Switcher + SLA + Export + Add CTA */}
        <div className="flex items-center gap-2">
          {/* Table / Board Switcher */}
          <div className="flex items-center rounded-lg border border-line bg-soft p-0.5">
            <button
              type="button"
              onClick={() => switchView("table")}
              title="Table view"
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md text-xs transition-colors",
                viewMode === "table" ? "bg-elevated text-brand shadow-xs font-semibold" : "text-ink-3 hover:text-ink"
              )}
            >
              <TableIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => switchView("board")}
              title="Board view"
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md text-xs transition-colors",
                viewMode === "board" ? "bg-elevated text-brand shadow-xs font-semibold" : "text-ink-3 hover:text-ink"
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Export CSV */}
          <button
            disabled={filteredLeads.length === 0}
            onClick={() => downloadLeadsCsv(filteredLeads, { agentName, campaignName, overdueReasons: (id) => overdueMap.get(id) ?? [] }, customers ? "customers" : "leads")}
            title="Export CSV"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 hover:bg-soft hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="h-3.5 w-3.5" />
          </button>

          {/* Upload Batch Button */}
          {!customers && (
            <button
              onClick={() => setUploadBatchOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-elevated px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-soft hover:text-ink shadow-xs transition-colors h-8 whitespace-nowrap"
            >
              <Plus className="h-3.5 w-3.5" /> Upload Batch
            </button>
          )}

          {/* Add Customer / Lead Button */}
          <button
            onClick={() => setAddLeadOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors h-8 whitespace-nowrap"
          >
            <Plus className="h-3.5 w-3.5" /> {customers ? "Add customer" : "Add lead"}
          </button>
        </div>
      </div>

      {/* ── Filters Row: Status Dropdown + Search + Filter Menu + Refresh (wraps onto its own line instead of overflowing when space is tight, e.g. a wider sidebar) ── */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-y border-line py-1.5 shrink-0">
        {/* Left: Status Filter — always a dropdown so the current status is
            readable and selectable whether the sidebar is expanded or collapsed. */}
        <div className="w-44 shrink-0">
          <Dropdown
            ariaLabel="Status filter"
            value={statusFilter}
            onChange={setStatusFilter}
            searchable
            options={[{ label: "All", value: ALL }, ...visibleStatuses.map(s => ({ label: s, value: s }))].map(tab => ({
              value: tab.value,
              label: `${tab.label} (${tab.value === ALL ? leads.length : leads.filter(l => leadStatus(l) === tab.value).length})`,
            }))}
          />
        </div>

        {/* Right: Sort + Search + Unified Filter Menu (Linear/Stripe style) + Refresh */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {/* Sort */}
          <div className="w-44">
            <Dropdown
              ariaLabel="Sort"
              value={sortOrder}
              onChange={(v) => setSortOrder(v as typeof sortOrder)}
              options={[...SORT_OPTIONS]}
            />
          </div>

          {/* Smart Search */}
          <div className="relative w-40 sm:w-48">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={customers ? "Search customer, phone…" : "Search lead, phone…"}
              className="w-full rounded-lg border border-line bg-elevated py-1 pl-7 pr-2 text-xs text-ink outline-none focus:border-brand transition-colors h-8"
            />
          </div>

          {/* Unified Filter Menu */}
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-lg border border-line bg-elevated px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:bg-soft",
                  activeFilterCount > 0 && "border-brand/40 bg-brand-soft text-brand font-semibold"
                )}
              >
                <ListFilter className="h-3.5 w-3.5" />
                <span>Filter</span>
                {activeFilterCount > 0 && (
                  <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 border-line bg-elevated p-0 shadow-xl" align="end">
              <div className="flex items-center justify-between border-b border-line px-4 py-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-3">Filters</span>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setSourceFilter("all");
                      selectCampaign(ALL);
                      setActiveBatch(ALL);
                      setAgentFilter(ALL);
                      setWorkflowFilter(ALL);
                      setDialedByFilter(ALL);
                      setCampaignNameFilter(ALL);
                      setWhatsappInstanceFilter(ALL);
                      setChannelFilter(new Set());
                      setDateRange({ ...presetRange("last_30"), preset: "last_30" });
                    }}
                    className="text-xs font-medium text-brand hover:underline"
                  >
                    Reset all
                  </button>
                )}
              </div>
              <div className="max-h-[400px] overflow-y-auto p-4">
                <div className="space-y-3.5">
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Source</label>
                  <Dropdown
                    ariaLabel="Source filter"
                    searchable
                    value={sourceFilter}
                    onChange={(value) => {
                      setSourceFilter(value as typeof sourceFilter);
                      if (value !== "campaign") selectCampaign(ALL);
                      if (value !== "batch") setActiveBatch(ALL);
                    }}
                    options={[
                      { value: "all", label: "All sources" },
                      { value: "campaign", label: "Campaign" },
                      { value: "batch", label: "Batch" },
                      { value: "manual", label: "Manual" },
                    ]}
                  />
                </div>
                {sourceFilter === "campaign" && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-ink-2">Campaign</label>
                    <Dropdown
                      ariaLabel="Campaign filter"
                      searchable
                      value={activeCampaign}
                      onChange={selectCampaign}
                      options={[{ value: ALL, label: "All campaigns" }, ...campaigns.map(c => ({ value: c.id, label: c.name }))]}
                    />
                  </div>
                )}
                {sourceFilter === "batch" && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-ink-2">Batch</label>
                    <Dropdown
                      ariaLabel="Batch filter"
                      searchable
                      value={activeBatch}
                      onChange={setActiveBatch}
                      options={[{ value: ALL, label: "All batches" }, ...batchOptions]}
                    />
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Assigned Agent</label>
                  <Dropdown
                    ariaLabel="Agent filter"
                    searchable
                    value={agentFilter}
                    onChange={setAgentFilter}
                    options={[
                      { value: ALL, label: "All agents" },
                      { value: "unassigned", label: "Unassigned" },
                      { value: "former", label: "Former agent" },
                      ...(team.data?.members.map(row => ({ value: row.id, label: row.name })) ?? [])
                    ]}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Workflow</label>
                  <Dropdown
                    ariaLabel="Workflow filter"
                    searchable
                    value={workflowFilter}
                    onChange={(v) => { setWorkflowFilter(v); setDialedByFilter(ALL); }}
                    options={[{ value: ALL, label: "All workflows" }, ...workflowOptions.map((w) => ({ value: w, label: w }))]}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Dialed By (Agent)</label>
                  <Dropdown
                    ariaLabel="Dialed by agent filter"
                    searchable
                    value={dialedByFilter}
                    onChange={setDialedByFilter}
                    options={[{ value: ALL, label: "All agents" }, ...dialedByOptions.map((a) => ({ value: a, label: a }))]}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Campaign</label>
                  <Dropdown
                    ariaLabel="WhatsApp campaign filter"
                    searchable
                    value={campaignNameFilter}
                    onChange={setCampaignNameFilter}
                    options={[{ value: ALL, label: "All campaigns" }, ...campaignNameOptions.map((c) => ({ value: c, label: c }))]}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">WhatsApp Instance</label>
                  <Dropdown
                    ariaLabel="WhatsApp instance filter"
                    searchable
                    value={whatsappInstanceFilter}
                    onChange={setWhatsappInstanceFilter}
                    options={[{ value: ALL, label: "All instances" }, ...whatsappInstanceOptions.map((i) => ({ value: i, label: i }))]}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Sent Via</label>
                  <div className="flex flex-wrap gap-1.5">
                    {CHANNELS.map((channel) => {
                      const active = channelFilter.has(channel);
                      return (
                        <button
                          key={channel}
                          type="button"
                          onClick={() => setChannelFilter((prev) => {
                            const next = new Set(prev);
                            active ? next.delete(channel) : next.add(channel);
                            return next;
                          })}
                          className={cn(
                            "rounded-lg border px-2.5 py-1 text-xs font-medium capitalize transition-colors",
                            active ? "border-brand/40 bg-brand-soft text-brand font-semibold" : "border-line text-ink-2 hover:bg-soft"
                          )}
                        >
                          {channel}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-2">Date Created</label>
                  <DateRangePicker value={dateRange} onChange={setDateRange} align="right" dropUp={true} />
                </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Reset & Refresh */}
          <button
            onClick={() => {
              setSourceFilter("all");
              selectCampaign(ALL);
              setActiveBatch(ALL);
              setAgentFilter(ALL);
              setWorkflowFilter(ALL);
              setDialedByFilter(ALL);
              setCampaignNameFilter(ALL);
              setWhatsappInstanceFilter(ALL);
              setChannelFilter(new Set());
              setStatusFilter(ALL);
              setSearch("");
              setShowOverdueOnly(false);
              setDateRange({ ...presetRange("last_30"), preset: "last_30" });
              void reload();
            }}
            title="Reset filters & refresh" 
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 hover:bg-soft hover:text-ink transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}

      {selectedIds.size > 0 && <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-brand/20 bg-brand-soft px-4 py-3 text-sm">
        {team.data?.permissions?.assign_leads && <select aria-label="Assign selected leads" value="choose" onChange={async e => { const id = e.target.value; try { for (const leadId of selectedIds) await crmApi.updateLead(leadId, { assignedAgentId: id || null }); await reload(); } catch (e) { setError((e as Error).message); } }} className="rounded-lg border border-line bg-elevated p-2"><option value="choose" disabled>Assign selected…</option><option value="">Unassigned</option>{team.data.members.filter(row => row.active).map(row => <option key={row.id} value={row.id}>{row.name}</option>)}</select>}
        <span className="font-semibold text-brand">{selectedIds.size} {customers ? (selectedIds.size === 1 ? "customer" : "customers") : (selectedIds.size === 1 ? "lead" : "leads")} selected</span>
        <label className="text-ink-2" htmlFor="bulk-lead-status">Set status</label>
        <button onClick={() => setBatchModalOpen(true)} className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white">Create batch</button>
        <select id="bulk-lead-status" defaultValue="" onChange={(event) => { applyBulkStatus(event.target.value); event.currentTarget.value = ""; }} className="rounded-lg border border-line bg-elevated px-3 py-1.5 text-sm text-ink">
          <option value="" disabled>Choose status</option>
          {visibleStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        <button
          onClick={() => setPendingBulkDelete(true)}
          className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-500/10 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete selected
        </button>
        <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-sm font-semibold text-ink-2 hover:text-brand">Clear selection</button>
      </div>}
      {viewMode === "board" && (
        <p className="mt-3 text-sm text-ink-2">Drag a card to move it through the pipeline.</p>
      )}
      {/* Full-width table (not a persistent split view) — clicking a row
          opens the detail panel as a slide-over drawer instead. Board mode
          groups the same filtered leads by status with drag-to-move. */}
      {viewMode === "table" ? (
      <div className="mt-2 overflow-x-auto rounded-xl border border-line bg-elevated">

        <div>
          {loading ? (
            <div className="p-8 text-center text-xs text-ink-3">Loading real CRM data…</div>
          ) : filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center">
              <Users className="h-6 w-6 text-ink-3" />
              <p className="text-sm font-semibold text-ink">
                {leads.length === 0
                  ? (customers ? "No customers yet" : "No leads yet")
                  : (customers ? "No customers match your search" : "No leads match your search")}
              </p>
              <p className="max-w-sm text-xs text-ink-3">
                {leads.length === 0
                  ? (customers ? "Create a campaign, then add your first customer to get started." : "Create a campaign, then add your first lead to get started.")
                  : "Try a different name, phone, or filter."}
              </p>
            </div>
          ) : (
            <table className="w-full min-w-[880px] text-left text-sm">
              <thead className="bg-soft text-[11px] font-semibold uppercase tracking-wider text-ink-3 border-b border-line">
                <tr>
                  <th className="w-10 px-3.5 py-2.5">
                    <input
                      type="checkbox"
                      aria-label={customers ? "Select all visible customers" : "Select all visible leads"}
                      checked={allVisibleSelected}
                      onChange={(event) => selectVisibleLeads(event.target.checked)}
                      className="h-4 w-4 accent-brand cursor-pointer"
                    />
                  </th>
                  <th className="px-3.5 py-2.5">{customers ? "Customer" : "Lead"}</th>
                  <th className="px-3.5 py-2.5">Phone</th>
                  <th className="px-3.5 py-2.5">Source</th>
                  <th className="px-3.5 py-2.5">Status</th>
                  <th className="px-3.5 py-2.5">Assigned</th>
                  <th className="px-3.5 py-2.5">Created</th>
                  <th className="px-3.5 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filteredLeads.map((l) => {
                  const displayName = l.name || l.contactId || (customers ? "Unnamed customer" : "Unnamed lead");
                  const contactId = contactIdOf(l);
                  return (
                    <tr
                      key={l.id}
                      onClick={() => openLead(l.id)}
                      className="cursor-pointer hover:bg-soft/70 transition-colors group"
                    >
                      <td className="px-3.5 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${displayName}`}
                          checked={selectedIds.has(l.id)}
                          onChange={() => toggleLead(l.id)}
                          className="h-4 w-4 accent-brand cursor-pointer"
                        />
                      </td>
                      <td className="px-3.5 py-2.5">
                        <div className="flex min-w-0 items-center gap-2">
                          <div className={cn("flex h-5 w-5 min-w-[20px] shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-2xs", avatarColorOf(l.id))}>
                            {initialsOf(l.name)}
                          </div>
                          <div className="flex min-w-0 items-baseline gap-2 truncate">
                            <span className="truncate text-sm font-semibold text-ink group-hover:text-brand transition-colors" title={displayName}>
                              {displayName}
                            </span>
                            <span
                              className="shrink-0 font-mono text-xs text-ink-3/80 hover:text-ink-2 transition-colors cursor-default"
                              title={`Contact ID: ${contactId}`}
                            >
                              {contactId}
                            </span>
                            <CopyButton
                              value={contactId}
                              className="shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-xs text-ink-2">{l.phone || "—"}</td>
                      <td className="whitespace-nowrap px-3.5 py-2.5 text-xs text-ink-2">{sourceLabel(l)}</td>
                      <td className="whitespace-nowrap px-3.5 py-2.5">
                        <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold", statusPillCls(leadStatus(l)))}>{leadStatus(l)}</span>
                        {overdueMap.has(l.id) && (
                          <span title={(overdueMap.get(l.id) ?? []).map(r => SLA_REASON_LABELS[r] ?? r).join(" · ")} className="ml-1 rounded-full bg-red-100 dark:bg-red-500/20 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-400">Overdue</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3.5 py-2.5">
                        {(() => {
                          const name = agentName(l.assignedAgentId);
                          if (name === "Former agent") {
                            return (
                              <span 
                                className="inline-flex items-center rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20"
                                title="This agent was removed from the organization. Reassign to an active agent."
                              >
                                Former agent
                              </span>
                            );
                          }
                          if (name === "Unassigned") {
                            return (
                              <span className="rounded-md bg-soft px-2 py-0.5 text-[11px] font-medium text-ink-3">
                                Unassigned
                              </span>
                            );
                          }
                          return (
                            <div className="flex items-center gap-1.5 text-ink-2">
                              <span className={cn(
                                "flex h-5 w-5 min-w-[20px] shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-2xs",
                                avatarColorOf(name)
                              )}>
                                {initialsOf(name)}
                              </span>
                              <span className="text-xs font-medium text-ink">{name}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-3.5 py-2.5 text-xs text-ink-3">
                        {formatDistanceToNow(new Date(l.createdAt), { addSuffix: true })}
                      </td>
                      <td className="whitespace-nowrap px-3.5 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <LeadCallButton
                            lead={l}
                            onLogged={() => reload({ silent: true })}
                            triggerClassName="flex items-center gap-1 rounded-lg bg-stone-900 text-white hover:bg-stone-800 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white px-2.5 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 transition-colors shadow-xs"
                          />
                          <button onClick={() => openLead(l.id)} className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink transition-colors" title="Edit">
                            <Pencil className="h-4 w-4" />
                          </button>
                          {team.data?.permissions?.assign_leads && (
                            <button onClick={() => setAssigningLead(l)} className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink transition-colors" title="Assign">
                              <UserCog className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => requestDelete(l.id)} className="rounded-lg p-1.5 text-ink-3 hover:bg-red-500/10 hover:text-red-600 transition-colors" title={customers ? "Delete customer" : "Delete lead"}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      ) : (
      <div className="mt-2">
        <style>{`.kanban-scroll{scrollbar-width:thin;scrollbar-color:#f0a57c transparent}.kanban-scroll::-webkit-scrollbar{width:8px}.kanban-scroll::-webkit-scrollbar-track{background:transparent}.kanban-scroll::-webkit-scrollbar-thumb{background:#f0a57c;border-radius:999px}.kanban-scroll::-webkit-scrollbar-thumb:hover{background:#ea580c}`}</style>
        <div className="pb-2">
          {loading ? (
            <div className="rounded-xl border border-line bg-elevated p-8 text-center text-xs text-ink-3">Loading real CRM data…</div>
          ) : filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-line bg-elevated p-12 text-center">
              <Users className="h-6 w-6 text-ink-3" />
              <p className="text-sm font-semibold text-ink">{leads.length === 0 ? "No leads yet" : "No leads match your search"}</p>
              <p className="max-w-sm text-xs text-ink-3">
                {leads.length === 0 ? "Create a campaign, then add your first customer to get started." : "Try a different name, phone, or filter."}
              </p>
            </div>
          ) : (
            <div className="flex items-stretch gap-4 overflow-x-auto pb-4">
              {boardGroups.map((group) => (
                <div
                  key={group.status}
                  onDragOver={(e) => { e.preventDefault(); setDropCol(group.status); }}
                  onDragLeave={() => setDropCol((prev) => (prev === group.status ? null : prev))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData("text/lead-id") || dragId;
                    if (id) void moveLeadToStatus(id, group.status);
                  }}
                  className={cn(
                    "flex w-[280px] shrink-0 flex-col rounded-xl transition-colors",
                    dropCol === group.status ? "bg-brand-soft/60" : "bg-transparent"
                  )}
                >
                  <div className="mb-2 flex shrink-0 items-center gap-2 px-1">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{
                        backgroundColor:
                          group.status === "Contacted" ? "#ea580c" :
                          group.status === "Interested" ? "#06b6d4" :
                          group.status === "Follow-up" ? "#d97706" :
                          group.status === "Qualified" ? "#8b5cf6" :
                          group.status === "Converted" ? "#16a34a" :
                          group.status === "Not interested" ? "#dc2626" :
                          group.status === "Fresh lead" ? "#a8a29e" : "#78716c",
                      }}
                    />
                    <h3 className="text-sm font-semibold text-ink">{group.status}</h3>
                    <span className="rounded-full bg-soft px-2 py-0.5 text-[11px] font-semibold text-ink-3">{group.items.length}</span>
                  </div>
                  <div className={cn(
                    "flex min-h-[120px] flex-1 flex-col gap-3 rounded-xl border border-dashed p-2",
                    dropCol === group.status ? "border-brand bg-brand-soft/40" : "border-line bg-elevated/40"
                  )}>
                    {group.items.length === 0 ? (
                      <p className="p-6 text-center text-xs text-ink-3">Drop a card here</p>
                    ) : (
                      group.items.map((l) => (
                        <div
                          key={l.id}
                          draggable={!dragBusy}
                          onDragStart={(e) => {
                            e.dataTransfer.setData("text/lead-id", l.id);
                            e.dataTransfer.effectAllowed = "move";
                            setDragId(l.id);
                          }}
                          onDragEnd={() => { setDragId(null); setDropCol(null); }}
                          onClick={() => openLead(l.id)}
                          className={cn(
                            "cursor-grab rounded-xl border border-line bg-elevated p-3 shadow-sm hover:border-brand/40 hover:shadow active:cursor-grabbing",
                            dragId === l.id ? "opacity-50" : "opacity-100"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white", avatarColorOf(l.id))}>
                              {initialsOf(l.name)}
                            </div>
                            <p className="truncate text-sm font-semibold text-ink">{l.name || l.contactId || (customers ? "Unnamed customer" : "Unnamed lead")}</p>
                          </div>
                          {(l.company || l.phone) && (
                            <div className="mt-1.5 space-y-0.5">
                              {l.company && <p className="truncate text-xs text-ink-2">{l.company}</p>}
                              {l.phone && <p className="truncate font-mono text-[11px] text-ink-3">{l.phone}</p>}
                            </div>
                          )}
                          <div className="mt-2 flex items-center justify-between border-t border-line pt-2 text-[11px] text-ink-3">
                            <span className="truncate">{sourceLabel(l)}</span>
                            <span className="ml-2 shrink-0">{formatDistanceToNow(new Date(l.createdAt), { addSuffix: true })}</span>
                          </div>
                          {overdueMap.has(l.id) && (
                            <span className="mt-1.5 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">Overdue</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {batchModalOpen && <CreateWorkflowBatchModal leads={leads.filter(lead => selectedIds.has(lead.id))} campaigns={campaigns} onClose={() => setBatchModalOpen(false)} />}

      {uploadBatchOpen && (
        <UploadContactBatchModal
          source="crm"
          onClose={() => setUploadBatchOpen(false)}
          onUploaded={() => { void reload({ silent: true }); }}
        />
      )}

      {addLeadOpen && (
        <LeadFormModal
          campaigns={campaigns}
          onClose={() => setAddLeadOpen(false)}
          onSave={addLead}
          onViewExisting={(leadId) => { setAddLeadOpen(false); navigate(`/crm/${customers ? "customers" : "leads"}/${leadId}`); }}
        />
      )}

      {assigningLead && (
        <AssignLeadModal
          lead={assigningLead}
          agents={team.data?.members ?? []}
          currentAgentName={agentName(assigningLead.assignedAgentId)}
          onClose={() => setAssigningLead(null)}
          onSave={(agentId) => updateLead(assigningLead.id, { assignedAgentId: agentId })}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title={customers ? "Delete customer" : "Delete lead"}
          body={`Remove "${pendingDelete.name}"? This can't be undone.`}
          confirmLabel="Delete"
          onConfirm={() => { deleteLead(pendingDelete.id); setPendingDelete(null); }}
          onClose={() => setPendingDelete(null)}
        />
      )}

      {pendingBulkDelete && (
        <ConfirmModal
          title={customers ? "Delete customers" : "Delete leads"}
          body={`Remove ${selectedIds.size} selected ${selectedIds.size === 1 ? (customers ? "customer" : "lead") : (customers ? "customers" : "leads")}? This can't be undone.`}
          confirmLabel="Delete"
          onConfirm={deleteSelectedLeads}
          onClose={() => setPendingBulkDelete(false)}
        />
      )}

    </div>
  );
}
